import { useRef, useState, useEffect } from 'react'
import defaultAvatar from '@/assets/images/avatar.png'
import { Icon } from '@/components/Icon'

interface AvatarProps {
  onAvatarChange?: (avatarUrl: string) => void
  initialAvatar?: string
}

const Avatar: React.FC<AvatarProps> = ({ onAvatarChange, initialAvatar }) => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [avatar, setAvatar] = useState<string>(initialAvatar || '')
  const [isLoading, setIsLoading] = useState<boolean>(false)

  // 当头像变化时通知父组件
  useEffect(() => {
    onAvatarChange?.(avatar)
  }, [avatar, onAvatarChange])

  /**
   * 生成随机的DiceBear头像URL
   * @param seed 可选的种子值，如果不提供会生成随机种子
   * @returns DiceBear头像URL
   */
  const generateAvatarUrl = (seed?: string): string => {
    const randomSeed = seed || Math.random().toString(36).substring(2, 15)
    return `https://api.dicebear.com/7.x/avataaars/svg?seed=${randomSeed}`
  }

  // 修改头像
  const handleAvatarChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onload = (ev) => {
        setAvatar(ev.target?.result as string)
      }
      reader.readAsDataURL(file)
    }
  }

  // 生成新的随机头像
  const handleGenerateRandomAvatar = () => {
    setIsLoading(true)
    const newAvatarUrl = generateAvatarUrl()

    // 创建一个新的Image对象来预加载图片
    const img = new Image()
    img.onload = () => {
      setAvatar(newAvatarUrl)
      setIsLoading(false)
    }
    img.onerror = () => {
      setIsLoading(false)
      // 如果加载失败，可以设置一个默认头像或显示错误提示
    }
    img.src = newAvatarUrl
  }

  return (
    <div className="mb-4 flex flex-col items-center">
      <div className="relative">
        <div
          className="w-16 h-16 rounded-[25px] flex items-center justify-center overflow-hidden border border-[#AA44CC6B]"
          style={{
            background: 'linear-gradient(180deg, #F1EBFF 0%, #F5F1FF 100%)',
          }}
          // onClick={() => fileInputRef.current?.click()}
        >
          {isLoading ? (
            <div className="w-16 h-16 flex items-center justify-center">
              <div className="flex items-center space-x-1">
                <div
                  className="w-1.5 h-1.5 bg-[#8752ff] rounded-full animate-bounce"
                  style={{ animationDelay: '0s' }}
                ></div>
                <div
                  className="w-1.5 h-1.5 bg-[#8752ff] rounded-full animate-bounce"
                  style={{ animationDelay: '0.1s' }}
                ></div>
                <div
                  className="w-1.5 h-1.5 bg-[#8752ff] rounded-full animate-bounce"
                  style={{ animationDelay: '0.2s' }}
                ></div>
              </div>
            </div>
          ) : (
            <img
              src={avatar || defaultAvatar}
              alt="avatar"
              className="w-16 h-16 object-cover"
            />
          )}
        </div>
        <button
          type="button"
          onClick={handleGenerateRandomAvatar}
          disabled={isLoading}
          className={`absolute -bottom-2 -right-2 w-8 h-8 rounded-full text-xs flex items-center justify-center transition-colors ${
            isLoading
              ? 'bg-gray-200 cursor-not-allowed'
              : 'bg-white hover:bg-gray-50 border border-gray-200 shadow-sm'
          }`}
          title={isLoading ? '生成中...' : '生成随机头像'}
        >
          <Icon name="stars" size={22} />
        </button>
      </div>
      <p className="flex items-center text-xs text-gray-500 mt-2">
        点击
        <Icon name="stars" size={22} />
        生成头像
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={handleAvatarChange}
      />
    </div>
  )
}

export default Avatar
